import React, { useEffect, useState } from "react";
import {
    COMMAND,
    GENERIC_REQUEST,
    getApiPath,
    jsonToQueryString,
    PARTICULAR_REQUEST_PARAMETER_LIST_GET,
    VIEW,
} from "../../api/ApiConst";
import ApiCaller from "../../api/ApiCaller";
import DynamicResponseView from "./DynamicResponseView";
import { TABLE_ATTRIBUTE_TYPES } from "../../settings/constants/types";
import DataTypeFactory from "../../utils/datatypes/DataTypeFactory";
import ListComponent from "../../utils/datatypes/ListComponent";

const DynamicRequestForm = () => {
    const [formData, setFormData] = useState({});
    const [responseData, setResponsedata] = useState(null);
    const [requestDetail, setRequestDetail] = useState(null);
    const [request, setRequest] = useState({ data: [] });

    const url = new URL(window.location.href);
    const requestId = url.pathname.split("/")[2];

    // Fetch request parameters
    const getRequestParameters = () => {
        const requestParameter = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll(
            "{request-id}",
            requestId
        );
        const jsonRequest = {
            page: 0,
            size: 100,
            searchFilters: [],
            sortRules: [],
        };

        ApiCaller.callPostApi(
            requestParameter,
            jsonRequest,
            {},
            (response) => {
                setRequest(response);
                initializeFormData(response.data);
            },
            (error) => {
                console.error("Error fetching parameters:", error);
            }
        );
    };

    // Fetch details of the selected request
    const fetchRequestDetail = (requestId) => {
        const singleRequestUrl = getApiPath(GENERIC_REQUEST, VIEW, requestId);

        ApiCaller.callGetApi(
            singleRequestUrl,
            {},
            (response) => {
                setRequestDetail(response);
            },
            (error) => {
                console.error("Error fetching request detail:", error);
            }
        );
    };

    useEffect(() => {
        fetchRequestDetail(requestId);
        getRequestParameters();
    }, []);

    // Initialize form fields dynamically
    const initializeFormData = (fields) => {
        const initialData = {};
        fields.forEach((field) => {
            switch (field.type) {
                case TABLE_ATTRIBUTE_TYPES.BOOLEAN:
                    initialData[field.name] = false;
                    break;
                case TABLE_ATTRIBUTE_TYPES.OBJECT:
                    initialData[field.name] = {};
                    break;
                case TABLE_ATTRIBUTE_TYPES.COLLECTION_OBJECT:
                    initialData[field.name] = null;
                    break;
                case TABLE_ATTRIBUTE_TYPES.MAP:
                case TABLE_ATTRIBUTE_TYPES.SET:
                    initialData[field.name] = [];
                    break;
                case TABLE_ATTRIBUTE_TYPES.DATE:
                case TABLE_ATTRIBUTE_TYPES.DATETIME:
                case TABLE_ATTRIBUTE_TYPES.TIME:
                    initialData[field.name] = "";
                    break;
                case TABLE_ATTRIBUTE_TYPES.INT:
                case TABLE_ATTRIBUTE_TYPES.DOUBLE:
                case TABLE_ATTRIBUTE_TYPES.FLOAT:
                case TABLE_ATTRIBUTE_TYPES.LONG:
                case TABLE_ATTRIBUTE_TYPES.SHORT:
                case TABLE_ATTRIBUTE_TYPES.NUMBER:
                case TABLE_ATTRIBUTE_TYPES.BYTE:
                case TABLE_ATTRIBUTE_TYPES.BIG_DECIMAL:
                case TABLE_ATTRIBUTE_TYPES.BIG_INT:
                    initialData[field.name] = "";
                    break;
                default:
                    initialData[field.name] = "";
            }
        });
        setFormData(initialData);
    };

    // Handle input change for all field types
    const handleInputChange = (fieldName, value) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        const requestUrl = getApiPath(requestDetail.name, COMMAND);

        if (requestDetail.type === "GET") {
            const finalUrl =
                Object.keys(formData).length > 0
                    ? `${requestUrl}?${jsonToQueryString(formData)}`
                    : requestUrl;

            ApiCaller.callGetApi(
                finalUrl,
                {},
                (response) => {
                    console.log("GET Response:", response);
                    setResponsedata(response);
                },
                (error) => {
                    console.error("Error on GET request:", error);
                }
            );
        } else {
            ApiCaller.callPostApi(
                requestUrl,
                formData,
                {},
                (response) => {
                    console.log("POST Response:", response);
                    setResponsedata(response);
                },
                (error) => {
                    console.error("Error on POST request:", error);
                }
            );
        }
    };

    // Render dynamic input fields based on type using DataTypeFactory
    const renderField = (field) => {
        const { name, type, description, required, config, listAttribute } = field;
        const value = formData[name];

        // Handle list attributes using ListComponent
        if (listAttribute) {
            return (
                <ListComponent
                    field={field}
                    value={value || []}
                    onChange={(newValue) => handleInputChange(name, newValue)}
                />
            );
        }

        // Handle OBJECT type with nested attributes
        if (type === TABLE_ATTRIBUTE_TYPES.OBJECT) {
            return (
                <DataTypeFactory
                    type={type}
                    value={value || {}}
                    onChange={(newValue) => handleInputChange(name, newValue)}
                    config={config}
                    mode="edit"
                />
            );
        }

        // Handle all other types using DataTypeFactory
        return (
            <DataTypeFactory
                type={type}
                value={value}
                onChange={(newValue) => handleInputChange(name, newValue)}
                config={config}
                mode="edit"
                placeholder={description || ""}
                required={required}
            />
        );
    };

    return (
        <div className="container mt-4">
            <h3>Request Form</h3>
            <form onSubmit={handleSubmit}>
                <div className="row">
                    {request.data.map((field) => (
                        <div className="col-md-6 mb-3" key={field.id}>
                            <label htmlFor={field.name} className="form-label">
                                {field.name} {field.required ? "*" : ""}
                                {field.description && (
                                    <small className="text-muted d-block">
                                        {field.description}
                                    </small>
                                )}
                            </label>
                            {renderField(field)}
                        </div>
                    ))}
                </div>
                <button type="submit" className="btn btn-primary">
                    Submit
                </button>
            </form>

            <div className="mt-4">
                <DynamicResponseView
                    response={responseData ?? {}}
                    requestMeta={requestDetail}
                />
            </div>
        </div>
    );
};

export default DynamicRequestForm;
